import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import {
  SupportTicketStatus,
  useSupportTickets,
  useUpdateTicket,
} from '@/hooks/useSupportChat';
import { TicketList } from '@/components/support/TicketList';
import { TicketConversation } from '@/components/support/TicketConversation';
import { CheckCircle2, UserCheck, XCircle } from 'lucide-react';

const Support = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: allTickets = [] } = useSupportTickets({ adminView: true });
  const updateTicket = useUpdateTicket();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | 'all'>('all');

  const tickets = useMemo(
    () =>
      allTickets.filter((tk) => statusFilter === 'all' || tk.status === statusFilter),
    [allTickets, statusFilter]
  );

  const activeTicket = allTickets.find((t) => t.id === activeId) || null;

  // Auto-passe le ticket en 'open' si admin commence à répondre
  useEffect(() => {
    if (activeTicket && activeTicket.status === 'pending_admin') {
      updateTicket.mutate({
        id: activeTicket.id,
        patch: { status: 'open', assigned_admin_id: user?.id ?? null },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Fetch user info for active ticket
  const { data: ticketUser } = useQuery({
    queryKey: ['ticket-user', activeTicket?.user_id],
    queryFn: async () => {
      if (!activeTicket) return null;
      const { data } = await supabase
        .from('users')
        .select('first_name, last_name, email, company_id, companies(name, type)')
        .eq('id', activeTicket.user_id)
        .single();
      return data;
    },
    enabled: !!activeTicket,
  });

  return (
    <div className="p-4 lg:p-6 h-[calc(100vh-4rem)]">
      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-3xl font-semibold text-[#37699E] font-['Lexend_Deca']">
          {t('supportChat.adminTitle')}
        </h1>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <SelectTrigger className="w-48 border-[#DBE2EA] rounded-lg">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('supportChat.filter.all')}</SelectItem>
            <SelectItem value="pending_admin">{t('supportChat.status.pending_admin')}</SelectItem>
            <SelectItem value="open">{t('supportChat.status.open')}</SelectItem>
            <SelectItem value="bot">{t('supportChat.status.bot')}</SelectItem>
            <SelectItem value="resolved">{t('supportChat.status.resolved')}</SelectItem>
            <SelectItem value="closed">{t('supportChat.status.closed')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 h-[calc(100%-4rem)] bg-white rounded-xl border border-[#DBE2EA] overflow-hidden">
        <div className="border-r border-[#DBE2EA] overflow-hidden">
          <TicketList
            tickets={tickets}
            activeId={activeId}
            onSelect={setActiveId}
            unreadSide="admin"
          />
        </div>

        <div className="overflow-hidden flex flex-col">
          {activeTicket ? (
            <>
              {ticketUser && (
                <div className="px-4 py-2 bg-[#EBF4F8] border-b border-[#DBE2EA] text-xs text-[#2D3748] flex items-center gap-3 flex-wrap">
                  <span>
                    <strong>
                      {ticketUser.first_name} {ticketUser.last_name}
                    </strong>{' '}
                    · {ticketUser.email}
                  </span>
                  {ticketUser.companies && (
                    <span className="text-[#5A6B7A]">
                      {(ticketUser.companies as any).name} ({(ticketUser.companies as any).type})
                    </span>
                  )}
                  <div className="ml-auto flex gap-2">
                    {activeTicket.status !== 'resolved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#14A38B] text-[#14A38B] hover:bg-[#14A38B]/10 rounded-md h-8"
                        onClick={() =>
                          updateTicket.mutate({
                            id: activeTicket.id,
                            patch: { status: 'resolved' },
                          })
                        }
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        {t('supportChat.markResolved')}
                      </Button>
                    )}
                    {activeTicket.status !== 'closed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#87ADD4] text-[#5A6B7A] hover:bg-[#DBE2EA] rounded-md h-8"
                        onClick={() =>
                          updateTicket.mutate({
                            id: activeTicket.id,
                            patch: { status: 'closed' },
                          })
                        }
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        {t('supportChat.close')}
                      </Button>
                    )}
                    {!activeTicket.assigned_admin_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] rounded-md h-8"
                        onClick={() =>
                          updateTicket.mutate({
                            id: activeTicket.id,
                            patch: { assigned_admin_id: user?.id ?? null },
                          })
                        }
                      >
                        <UserCheck className="h-4 w-4 mr-1" />
                        {t('supportChat.assignMe')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-hidden">
                <TicketConversation ticket={activeTicket} asAdmin />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[#5A6B7A]">
              {t('supportChat.selectTicket')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Support;
